import { Plus, Trash2, Check, X as XIcon, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';

import { supabase } from '../../../../lib/supabase';
import { useCurrentCompany } from '../../../../hooks/useCurrentCompany';

interface Sport {
  id: string;
  sport_name: string;
  is_primary: boolean | null;
}

export function SportsManager() {
  const { data: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const sportsQuery = useQuery({
    queryKey: ['company-sports', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const { data, error: qErr } = await supabase
        .from('company_sports')
        .select('id, sport_name, is_primary')
        .eq('company_id', companyId)
        .order('sport_name');
      if (qErr) throw qErr;
      return (data || []) as Sport[];
    },
    enabled: !!companyId,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['company-sports', companyId] });

  const create = useMutation({
    mutationFn: async (name: string) => {
      if (!companyId) throw new Error('No company selected');
      const { error: insErr } = await supabase
        .from('company_sports')
        .insert({ company_id: companyId, sport_name: name });
      if (insErr) throw insErr;
    },
    onSuccess: () => {
      setNewName('');
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const update = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error: updErr } = await supabase
        .from('company_sports')
        .update({ sport_name: name })
        .eq('id', id);
      if (updErr) throw updErr;
    },
    onSuccess: () => {
      setEditingId(null);
      setEditName('');
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: delErr } = await supabase
        .from('company_sports')
        .delete()
        .eq('id', id);
      if (delErr) throw delErr;
    },
    onSuccess: invalidate,
    onError: (e: Error) => setError(e.message),
  });

  const handleAdd = () => {
    setError(null);
    const name = newName.trim();
    if (!name) return;
    create.mutate(name);
  };

  const handleDelete = (id: string, name: string) => {
    setError(null);
    if (!window.confirm(`Delete "${name}"? This will fail if any active class uses it.`)) return;
    remove.mutate(id);
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="New sport name…"
          className="flex-1 bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
        />
        <button
          onClick={handleAdd}
          disabled={create.isPending || !newName.trim()}
          className="flex items-center gap-1 bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
        >
          {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add
        </button>
      </div>

      {error && (
        <div className="text-red-400 text-sm bg-red-900/20 border border-red-600/40 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <div className="border border-[#363232] rounded-lg overflow-hidden">
        {sportsQuery.isLoading ? (
          <div className="p-6 flex justify-center">
            <Loader2 size={20} className="animate-spin text-[#A9A3A3]" />
          </div>
        ) : sportsQuery.data?.length === 0 ? (
          <p className="p-4 text-sm text-[#A9A3A3] text-center">No sports yet.</p>
        ) : (
          sportsQuery.data?.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between px-3 py-2 border-b border-[#363232] last:border-0"
            >
              {editingId === s.id ? (
                <>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 bg-[#111010] border border-[#504A4A] rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421] mr-2"
                    autoFocus
                  />
                  <button
                    onClick={() => update.mutate({ id: s.id, name: editName.trim() })}
                    disabled={update.isPending || !editName.trim()}
                    className="p-1.5 text-green-400 hover:bg-green-900/20 rounded"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(null);
                      setEditName('');
                    }}
                    className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                  >
                    <XIcon size={14} />
                  </button>
                </>
              ) : (
                <>
                  <span className="text-sm text-[#EBFFEE]">{s.sport_name}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setEditingId(s.id);
                        setEditName(s.sport_name);
                      }}
                      className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(s.id, s.sport_name)}
                      disabled={remove.isPending}
                      className="p-1.5 text-[#A9A3A3] hover:text-red-400 hover:bg-red-900/20 rounded disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
